import { useRef, useState } from 'react'

interface Options<T> {
  isEqual?: (a: T, b: T) => boolean
  delay?: number
}

export const useDebouncedValue = <T>(currentValue: T, options: Options<T> = {}): T => {
  const [debouncedValue, setValue] = useState<T>(currentValue)

  const { isEqual = nativeIsEqual, delay = 500 } = options

  const delayRef = useRef(delay)
  delayRef.current = delay

  const timeoutIdRef = useRef<number>(null)
  const setValueDebounced = useRef((newValue: T) => {
    if (typeof window === 'undefined') return
    const { clearTimeout, setTimeout } = window

    if (timeoutIdRef.current) {
      // Clear any running timeouts
      clearTimeout(timeoutIdRef.current)
    }

    // Schedule a new call
    const newTimeoutId = setTimeout(() => setValue(newValue), delayRef.current)

    timeoutIdRef.current = newTimeoutId
  }).current

  const previousValueRef = useRef<T>(currentValue)
  if (!isEqual(previousValueRef.current, currentValue)) {
    setValueDebounced(currentValue)
    previousValueRef.current = currentValue
  }

  return debouncedValue
}

const nativeIsEqual = (a: unknown, b: unknown) => a === b
